import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Phone, Mail, X } from "lucide-react";
import { CONTACT } from "../data/content";
import { useLang } from "../i18n";
import { EASE } from "./Reveal";

import { WhatsAppIcon } from "./PhoneLinks";

export const ContactHub = () => {
  const [open, setOpen] = useState(false);
  const { t } = useLang();
  const wa = `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(t.hub.prefill)}`;
  const options = [
    { id: "whatsapp", href: wa, Icon: WhatsAppIcon, label: t.hub.whatsapp, sub: t.hub.whatsappSub, cls: "bg-[#25D366] text-white", external: true },
    { id: "call", href: `tel:+${CONTACT.whatsapp}`, Icon: Phone, label: t.hub.call, sub: t.hub.callSub, cls: "bg-[#0F5C8C]/10 border border-[#0F5C8C]/30 text-[#0F5C8C]" },
    { id: "email", href: `mailto:${CONTACT.email}`, Icon: Mail, label: t.hub.email, sub: t.hub.emailSub, cls: "bg-gradient-brand text-white" },
  ];

  return (
    <div className="nv-contact-hub fixed bottom-5 right-4 z-40 flex flex-col items-end gap-3 transition-[bottom] duration-300 sm:right-7 lg:bottom-7" data-testid="contact-hub">
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ duration: 0.28, ease: EASE }}
            className="w-[min(280px,calc(100vw-2rem))] border border-[#C8D3DB] glass p-3 shadow-[0_20px_60px_-20px_rgba(18,19,22,0.35)]"
            data-testid="contact-hub-panel"
          >
            <p className="px-2 pb-2 pt-1 font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#5E7280]">{t.hub.title}</p>
            <div className="space-y-1.5">
              {options.map((o, i) => (
                <motion.a
                  key={o.id}
                  href={o.href}
                  target={o.external ? "_blank" : undefined}
                  rel={o.external ? "noopener noreferrer" : undefined}
                  data-testid={`contact-hub-${o.id}`}
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.3, ease: EASE }}
                  className="group flex min-h-[52px] items-center gap-3 glass p-2.5 transition-colors hover:bg-white/10"
                >
                  <span className={`flex h-10 w-10 shrink-0 items-center justify-center ${o.cls}`}>
                    <o.Icon size={18} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-[#0E1B24]">{o.label}</span>
                    <span className="block truncate text-xs text-[#5E7280]">{o.sub}</span>
                  </span>
                </motion.a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="contact-hub-toggle"
        aria-expanded={open}
        aria-label={open ? t.hub.close : t.hub.open}
        className={`group relative flex h-14 items-center gap-2 pl-4 pr-5 text-sm font-bold shadow-[0_12px_40px_-12px_rgba(193,39,45,0.6)] transition-all duration-300 hover:-translate-y-0.5 ${
          open ? "bg-[#FFFFFF] border border-[#C8D3DB] text-[#0E1B24]" : "bg-[#25D366] text-[#062B17]"
        }`}
      >
        {open ? <X size={20} /> : <WhatsAppIcon size={22} />}
        <span className="hidden sm:inline">{open ? t.hub.close : t.hub.open}</span>
        {!open && <span className="absolute -right-1 -top-1 h-3 w-3 animate-pulse bg-[#0F5C8C]" />}
      </button>
    </div>
  );
};

export default ContactHub;
